// src/App.js
import React, { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, useNavigate, useParams } from "react-router-dom";
import HomePage from "./pages/Home";
import TravelPage from "./pages/Travel";
import Test from "./pages/Test";
import "./App.css";
import AuthModal from "./components/AuthModal";
import Navbar from "./components/Navbar";
import TripModal from "./components/TripModal";
import GuideModal from "./components/GuideModal";
import Footer from "./components/Footer";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import Contact from "./pages/Contact";
import { setFavicon, setTitle } from "./utils/setMeta";
import { addTrip } from "./services/trips";
import { useAuth } from "./context/AuthContext";
import { normalizeHandle } from "./utils/handle";
import { auth } from "./firebase";
import { useTheme } from "./context/ThemeContext";
import ExplorePage from "./pages/Explore";
import { ToastProvider } from "./components/ToastProvider";
import AiTripDetails from "./pages/AiTripDetails";
import { AiTripProvider } from "./context/AiTripContext";
import Guide from "./pages/Guide";
import TripsConfig from "./components/TripsConfig";
import { doc, getDoc } from "firebase/firestore";
import { db } from "./firebase";
import Place from "./components/Place";
// OR "./components/Place" depending on where it lives

const DEFAULT_API = "https://us-central1-travel-6c761.cloudfunctions.net/api";
const API_BASE =
  process.env.REACT_APP_API_BASE ||
  (process.env.REACT_APP_USE_EMULATOR === "true"
    ? process.env.REACT_APP_EMULATOR_URL || "http://127.0.0.1:5001/travel-6c761/us-central1/api"
    : DEFAULT_API);

function TravelPageWithHandle() {
  const params = useParams();

  const raw =
    typeof params.maybeHandle === "string"
      ? decodeURIComponent(params.maybeHandle)
      : "";

  const cleaned = raw.replace(/^\/+|\/+$/g, "");

  const [isGuide, setIsGuide] = useState(null);

  // detect @handle
  const isHandle = cleaned.startsWith("@");
  const handle = isHandle ? normalizeHandle(cleaned.slice(1)) : null;

  // root-level slug candidate
  const slug = !isHandle ? cleaned : null;

  useEffect(() => {
    if (!slug) {
      setIsGuide(false);
      return;
    }

    let cancelled = false;

    const checkSlug = async () => {
      try {
        const ref = doc(db, "slugs", slug);
        const snap = await getDoc(ref);

        if (!cancelled) {
          // if slug exists AND has guideId → guide
          setIsGuide(snap.exists() && !!snap.data()?.guideId);
        }
      } catch {
        if (!cancelled) setIsGuide(false);
      }
    };

    checkSlug();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  // ⏳ loading
  if (slug && isGuide === null) {
    return null;
  }

  // 🏠 Home
  if (!cleaned) {
    return <HomePage />;
  }

  // 📘 GUIDE
  if (isGuide) {
    return <Guide />;
  }

  // 📍 PLACE (or anything else with a slug)
  if (slug) {
    return <Place />;
  }

  // 👤 Profile / Trips
  return <TravelPage externalHandle={handle} />;
}

function NavbarWithNavigate({ onOpenAuth, onAddTrip, onAddGuide }) {
  const navigate = useNavigate();

  const handleViewTrips = (handle) => {
    if (!handle) {
      navigate("/");
      return;
    }
    const raw = String(handle).replace(/^@/, "");
    const normalized = normalizeHandle(raw);
    if (!normalized) {
      navigate("/");
      return;
    }
    navigate(`/@${normalized}`);
  };

  const handleNavigateHome = () => {
    navigate("/");
  };

  return (
    <Navbar
      onOpenAuth={onOpenAuth}
      onAddTrip={onAddTrip}
      onAddGuide={onAddGuide}
      onViewTrips={handleViewTrips}
      onNavigateHome={handleNavigateHome}
    />
  );
}

function App() {
  const [authMode, setAuthMode] = useState(null);
  const [showAddTripModal, setShowAddTripModal] = useState(false);
  const [savingTrip, setSavingTrip] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const { currentUser } = useAuth();
  const { theme } = useTheme();
  const [showAddGuideModal, setShowAddGuideModal] = useState(false);
  const [showTripsConfig, setShowTripsConfig] = useState(false);
  const { profile } = useAuth();

  useEffect(() => {
    if (currentUser) {
      setTitle(`Jift | Welcome ${currentUser.displayName || "User"}`);
    } else {
      setTitle("Jift | Travel Planner");
    }
    setFavicon(theme === "dark" ? "/favicon-dark32.png" : "/favicon-light32.png");
  }, [currentUser, theme]);

  useEffect(() => {
    const onOpenFromAnywhere = () => setShowAddTripModal(true);
    window.addEventListener("jift:openAddTrip", onOpenFromAnywhere);
    return () => window.removeEventListener("jift:openAddTrip", onOpenFromAnywhere);
  }, []);

  useEffect(() => {
    const onOpenGuide = () => setShowAddGuideModal(true);
    window.addEventListener("jift:openAddGuide", onOpenGuide);
    return () => window.removeEventListener("jift:openAddGuide", onOpenGuide);
  }, []);

  useEffect(() => {
    const handler = () => setShowTripsConfig(true);
    window.addEventListener("jift:openTripsConfig", handler);
    return () => window.removeEventListener("jift:openTripsConfig", handler);
  }, []);

  const openAddGuide = () => {
    if (!currentUser?.uid) {
      setAuthMode("login");
      return;
    }
    setShowAddGuideModal(true);
  };

  const closeAddGuide = () => {
    setShowAddGuideModal(false);
  };

  const openAuth = (mode) => setAuthMode(mode);
  const closeAuth = () => setAuthMode(null);

  const openAddTrip = () => {
    if (!currentUser?.uid) {
      setAuthMode("login");
      return;
    }
    setShowAddTripModal(true);
  };

  const closeAddTrip = () => {
    setShowAddTripModal(false);
    setSaveError(null);
  };

  const handleSaveTrip = async (tripData) => {
    if (!currentUser?.uid) {
      setSaveError("You must be logged in to create a trip.");
      setAuthMode("login");
      return;
    }

    setSavingTrip(true);
    setSaveError(null);

    try {
      const id = await addTrip(tripData, currentUser.uid);
      closeAddTrip();
      return id;
    } catch (err) {
      console.error("App - handleSaveTrip create error:", err);
      setSaveError(err?.message || "Failed to save trip.");
      throw err;
    } finally {
      setSavingTrip(false);
    }
  };

  const ownerHandle =
    currentUser && (currentUser.displayName || currentUser.email || currentUser.uid)
      ? normalizeHandle(
        currentUser.displayName ||
        (currentUser.email ? currentUser.email.split("@")[0] : "") ||
        (currentUser.uid || "")
      )
      : null;

  return (
    <BrowserRouter>
      <ToastProvider>
        <AiTripProvider>
          <NavbarWithNavigate onOpenAuth={openAuth} onAddTrip={openAddTrip} onAddGuide={openAddGuide} />

          <main style={{ minHeight: "calc(100vh - 160px)" }}>
            <Routes>
              <Route path="/debug" element={<div>Debug route</div>} />
              <Route path="/test" element={<Test />} />
              <Route path="/" element={<HomePage />} />

              <Route path="/travel/:handle/:slug?" element={<TravelPageWithHandle />} />
              <Route path="/travel/:slug" element={<TravelPageWithHandle />} />

              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/contact" element={<Contact />} />

              <Route path="/:maybeHandle/:slug?" element={<TravelPageWithHandle />} />

              <Route path="/explore" element={<ExplorePage />} />
              <Route path="/ai-trip" element={<AiTripDetails />} />

              <Route path="*" element={<HomePage />} />


            </Routes>
          </main>

          {authMode && (
            <AuthModal mode={authMode} onClose={closeAuth} onSwitch={(m) => setAuthMode(m)} />
          )}

          <TripModal
            open={showAddTripModal}
            onClose={closeAddTrip}
            onSave={handleSaveTrip}
            saving={savingTrip}
            currentUserId={currentUser?.uid || null}
            ownerHandle={ownerHandle}
          />
          {currentUser && profile?.role === "superadmin" && (
            <GuideModal
              open={showAddGuideModal}
              currentUserId={currentUser.uid}
              onClose={closeAddGuide}
            />
          )}
          {currentUser && profile?.role === "superadmin" && showTripsConfig && (
            <TripsConfig
              open={showTripsConfig}
              onClose={() => setShowTripsConfig(false)}
            />
          )}
          {/* {currentUser && showTripsConfig && (
            <TripsConfig
              open={showTripsConfig}
              onClose={() => setShowTripsConfig(false)}
            />
          )} */}

          {saveError && (
            <div
              style={{
                position: "fixed",
                bottom: 20,
                right: 20,
                background: "#ffe6e6",
                padding: 12,
                borderRadius: 8,
              }}
            >
              <strong>Error:</strong> {saveError}
            </div>
          )}

          <Footer />
        </AiTripProvider>
      </ToastProvider>
    </BrowserRouter>
  );
}

export default App;
